import { safeAddDoc } from '../utils';
import React from 'react';
import {
  Calendar as CalendarIcon,
  Rss,
  Youtube,
  Film,
  Camera,
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  CheckSquare,
  MessageSquare as MessageSquareIcon,
  FileText,
  Bell,
  Users,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';

import { db, useEmulator } from '../firebase';
import { appId } from '../constants';
import { collection, serverTimestamp } from 'firebase/firestore';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Avatar from './Avatar';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectDataContext';
import ListenBrainzFeed from './ListenBrainzFeed';
import EnvDropdown from './EnvDropdown';

const MUTED_COLORS = [
  { id: 'default', hex: '#ffffff' },
  { id: 'red', hex: '#fecaca' },
  { id: 'orange', hex: '#fed7aa' },
  { id: 'yellow', hex: '#fef08a' },
  { id: 'green', hex: '#bbf7d0' },
  { id: 'blue', hex: '#bfdbfe' },
  { id: 'purple', hex: '#ddd6fe' },
  { id: 'pink', hex: '#fbcfe8' },
];

const DMUserItem = ({
  targetUser,
  user,
  channels,
  activeChannelId,
  currentView,
  dispatch,
  isChatOpen,
  toggleChat,
  allUsers,
  handleShowProfile,
  navigate,
  isOnline,
}) => {
  const dmChannel = (channels || []).find(
    (c) =>
      c.type === 'dm' &&
      (c.participants || []).includes(user?.uid) &&
      (c.participants || []).includes(targetUser.id)
  );
  const isActive = dmChannel && activeChannelId === dmChannel.id && currentView === 'workspace';

  const handleStartDM = async () => {
    if (!user || !targetUser || user.uid === targetUser.id) return;
    let dmId = dmChannel?.id;
    if (!dmId) {
      try {
        const targetUid = user?.uid || 'test-uid-123';
        const newDM = await safeAddDoc(
          collection(db, 'artifacts', appId, 'public', 'data', 'channels'),
          {
            type: 'dm',
            participants: [targetUid, targetUser.id],
            createdAt: serverTimestamp(),
            lastActivity: serverTimestamp(),
            createdBy: targetUid,
            order: Date.now(),
          }
        );
        dmId = newDM.id;
      } catch (err) {
        console.error(err);
        return;
      }
    }

    if (window.innerWidth >= 1024) {
      dispatch({ type: 'SET_OPEN_DM', payload: dmId });
    } else {
      if (toggleChat && !isChatOpen) {
        toggleChat();
      }

      navigate(`/p/${dmId}/chat${window.location.search}`);
    }
  };

  const userData = (allUsers || []).find((u) => u.id === targetUser.id) || targetUser;

  return (
    <div className="flex items-center w-full">
      <button
        data-testid="dm-user-btn"
        onClick={handleStartDM}
        className={`flex-1 flex items-center px-2 py-1 text-sm rounded transition-colors ${isActive ? 'bg-zinc-100 text-zinc-900 font-medium' : 'text-zinc-600 hover:bg-zinc-50'}`}
      >
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleShowProfile(userData);
          }}
          className="relative p-0.5 hover:bg-black/5 rounded-md transition-colors mr-2 cursor-pointer"
        >
          <Avatar src={userData.avatar} size={20} />
          {isOnline && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border-[1.5px] border-white shadow-sm" />
          )}
        </div>
        <span className={`truncate flex-1 text-left`}>{userData.name || 'Unknown'}</span>
      </button>
    </div>
  );
};

const ProjectSubLinks = ({
  projectId,
  navigate,
  channels,
  dispatch,
  isActive,
}) => {
  const channel = (channels || []).find((c) => c.id === projectId);
  const isDM = channel?.type === 'dm';

  const handleLinkClick = (module) => {
    const path = `/p/${projectId}/${module}`;
    const search = window.location.search || '';
    const fullPath = path + search;

    if (window.innerWidth >= 1024) {
      window.open(fullPath, '_blank');
    } else {
      navigate(fullPath);
    }
  };

  return (
    <div className="flex items-center justify-around gap-1 px-3 py-2 bg-zinc-50/50 border-b border-zinc-100 animate-in fade-in slide-in-from-top-2 duration-300">
      <button
        data-testid={`project-chat-link-${projectId}`}
        onClick={(e) => {
          e.stopPropagation();
          handleLinkClick('chat');
        }}
        className="flex items-center justify-center p-1.5 rounded-lg hover:bg-white transition-all group"
        title="Chat"
      >
        <div className="w-8 h-8 rounded-md bg-blue-500/10 flex items-center justify-center text-blue-600 shadow-sm border border-blue-500/5 group-hover:scale-110 transition-transform">
          <MessageSquareIcon size={16} strokeWidth={2.5} />
        </div>
      </button>
      {!isDM && (
        <>
          <button
            data-testid={`project-tasks-link-${projectId}`}
            onClick={(e) => {
              e.stopPropagation();
              handleLinkClick('tasks');
            }}
            className="flex items-center justify-center p-1.5 rounded-lg hover:bg-white transition-all group"
            title="Tasks"
          >
            <div className="w-8 h-8 rounded-md bg-green-500/10 flex items-center justify-center text-green-600 shadow-sm border border-green-500/5 group-hover:scale-110 transition-transform">
              <CheckSquare size={16} strokeWidth={2.5} />
            </div>
          </button>
          <button
            data-testid={`project-notes-link-${projectId}`}
            onClick={(e) => {
              e.stopPropagation();
              handleLinkClick('notes');
            }}
            className="flex items-center justify-center p-1.5 rounded-lg hover:bg-white transition-all group"
            title="Notepad"
          >
            <div className="w-8 h-8 rounded-md bg-purple-500/10 flex items-center justify-center text-purple-600 shadow-sm border border-purple-500/5 group-hover:scale-110 transition-transform">
              <FileText size={16} strokeWidth={2.5} />
            </div>
          </button>
        </>
      )}
    </div>
  );
};

const SortableProjectItem = ({
  channel,
  depth,
  index,
  isActive,
  isColored,
  channelColorHex,
  displayName,
  hasChildren,
  isExpanded,
  dispatch,
  renderProjectTree,
  editingChannelId,
  getProjectPath,
  channels,
  isTouch,
  activeButtonsId,
  currentView,
}) => {
  const navigate = useNavigateWithParams();

  const stateId = channel?.id;
  const sortId = channel?.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortId || 'empty',
    data: { type: 'channel', channel, isStarredList: false },
  });

  if (!channel) return null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: isDragging ? 'none' : 'pan-y',
  };
  const isBase = !channel.parentId;
  const showButtons = activeButtonsId === stateId;

  return (
    <div
      ref={setNodeRef}
      style={style}
      key={sortId}
      className={`w-full ${isBase ? 'mb-1' : 'mt-0.5'}`}
    >
      <div
        className={`relative group w-full rounded-xl transition-all ${isBase ? 'border border-zinc-200 bg-white shadow-sm overflow-hidden' : 'rounded-none'}`}
      >
        <div
          {...attributes}
          {...listeners}
          onClick={(e) => {
            if (isDragging) return;
            if (e.target.closest('button')) return;

            const path = getProjectPath(channel.id, channels || []).map((c) => c.id);
            // Single-expand logic + path awareness
            dispatch({ type: 'SET_ACTIVE_BUTTONS', payload: { id: stateId, pathIds: path } });
            dispatch({ type: 'SET_ACTIVE_CHANNEL', payload: channel.id });
            if (currentView === 'database') {
              window.location.href = `/p/${channel.id}${window.location.search}`;
            } else {
              navigate(`/p/${channel.id}${window.location.search}`);
            }
          }}
          data-testid="sidebar-project-item"
          data-project-id={channel.id}
          className={`w-full flex items-center justify-between px-2 py-2 text-sm transition-all select-none outline-none rounded-xl border-2 cursor-pointer ${isActive ? 'font-bold bg-zinc-100 text-zinc-900 border-zinc-800 shadow-sm' : 'text-zinc-600 hover:bg-zinc-50 border-transparent'}`}
          style={isColored && !isActive ? { borderLeft: `4px solid ${channelColorHex}` } : {}}
        >
          <div className="flex items-center overflow-hidden min-w-0 pointer-events-none">
            {isBase ? (
              <div
                className="w-2 h-2 rounded-full mr-2 flex-shrink-0 transition-all"
                style={{ backgroundColor: channelColorHex }}
              />
            ) : (
              <div className="flex gap-0.5 mr-1.5 flex-shrink-0">
                {Array.from({ length: depth }).map((_, i) => (
                  <div key={i} className="w-1 h-1 rounded-full bg-zinc-300" />
                ))}
              </div>
            )}
            <button
              data-testid="sidebar-project-link"
              onClick={(e) => {
                e.stopPropagation();
                const path = getProjectPath(channel.id, channels || []).map((c) => c.id);
                dispatch({ type: 'TOGGLE_EXPANDED', payload: { id: stateId, pathIds: path } });
                dispatch({ type: 'SET_ACTIVE_CHANNEL', payload: channel.id });
                if (currentView === 'database') {
                  window.location.href = `/p/${channel.id}${window.location.search}`;
                } else {
                  navigate(`/p/${channel.id}` + window.location.search);
                }
              }}
              className="flex flex-col min-w-0 flex-1 text-left hover:opacity-70 transition-opacity"
            >
              <span
                data-testid={`project-name-${channel.id}`}
                className={`whitespace-nowrap overflow-hidden truncate ${isBase ? 'font-black uppercase tracking-tight text-[11px] text-zinc-800' : 'text-xs'}`}
              >
                {displayName}
              </span>
            </button>
          </div>
          <div
            className={`flex items-center transition-opacity flex-shrink-0 ${isTouch ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid={`edit-project-btn-${channel.id}`}
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: 'OPEN_EDIT_CHANNEL', payload: { ...channel, id: stateId } });
              }}
              className="p-0.5 hover:bg-zinc-200 rounded text-zinc-400 hover:text-zinc-900"
              title="Edit Project"
            >
              <Pencil size={12} strokeWidth={2.5} />
            </button>
            <button
              data-testid={`add-subproject-btn-${channel.id}`}
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: 'OPEN_CREATE_CHANNEL', payload: channel.id });
              }}
              className="p-0.5 hover:bg-zinc-200 rounded text-zinc-400 hover:text-zinc-900"
              title="Add Sub-Project"
            >
              <Plus size={14} strokeWidth={2.5} />
            </button>
          </div>
        </div>
        {showButtons && (
          <ProjectSubLinks
            projectId={channel.id}
            navigate={navigate}
            channels={channels}
            dispatch={dispatch}
            isActive={isActive}
          />
        )}
        {isExpanded && (
          <div className="flex flex-col">
            <div className="pb-1">{renderProjectTree(channel.id, depth + 1)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const Sidebar = ({
  state,
  activeChannelId,
  currentView,
  dispatch,
  onlineUsers = [],
  offlineUsers = [],
  isCreatingChannel,
  newChannelName,
  newChannelColor,
  createError,
  activeColorHex,
  handleCreateChannel,
  handleDeleteChannel,
  handleConfirmEditChannel,
  handleExportProject,
  getProjectPath,
  getRootProject,
  isChatOpen,
  toggleChat,
  isUpdateAvailable,
  visibleCalendars = [],
  enabledCalendars = [],
  googleCalendars = [],
  handleGoHome,
  handleGoTikiBar,
  handleShowProfile,
  isTouch,
  saveUserProfile,
  isCompact,
}) => {
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const navigate = useNavigateWithParams();
  const location = useLocation();
  const { expandedIds, activeButtonsId, editingChannelId } = state || {};
  const [isFriendsListOpen, setIsFriendsListOpen] = React.useState(() => {
    return localStorage.getItem('sidebarFriendsOpen') !== 'false';
  });
  const [isLinksExpanded, setIsLinksExpanded] = React.useState(() => {
    return localStorage.getItem('sidebarLinksExpanded') !== 'false';
  });
  const { user, username, profile } = useAuth();
  const { channels, allUsers, notifications } = useProject();
  const isAdmin = user?.email === 'brett.gaddy@gmail.com';

  const userAvatar = profile?.avatar || '👤';

  const myFriends = profile?.friends || [];
  const myEmail = user?.email?.toLowerCase();

  const isMutualFriend = (u) => {
    if (!u) return false;
    const theirEmail = u.email?.toLowerCase();
    const theirFriends = u.friends || [];
    return myFriends.includes(theirEmail) && theirFriends.includes(myEmail);
  };

  const onlineFriends = onlineUsers.filter(isMutualFriend);
  const offlineFriends = offlineUsers.filter(isMutualFriend);

  const combinedFriendsList = [
    ...onlineFriends.map(f => ({ ...f, isOnline: true })),
    ...offlineFriends.map(f => ({ ...f, isOnline: false }))
  ];

  const isHomeActive = currentView === 'home';
  const isCalendarActive = currentView === 'calendar';
  const isWeatherActive = currentView === 'weather';
  const isWatchActive = currentView === 'watch';
  const isRSSActive = currentView === 'rss';
  const isFilmTVActive = currentView === 'film-tv';
  const isMediaActive = currentView === 'media';

  const renderProjectTree = (pId = null, depth = 0) => {
    let items = (channels || []).filter((c) => {
      if (!c || !c.name || !c.name.trim()) return false;
      if ((c.parentId || null) !== pId) return false;
      if (c.id === 'general_chat_global') return false;
      const participants = c.participants || [c.createdBy];
      const uid = user?.uid || 'test-uid-123';
      return participants.includes(uid);
    });

    items.sort((a, b) => (a.order || 0) - (b.order || 0));

    if (items.length === 0) return null;

    return (
      <SortableContext
        items={items.map((c) => c.id)}
        strategy={verticalListSortingStrategy}
      >
        {items.map((channel, idx) => {
          const isActive = activeChannelId === channel.id && currentView === 'workspace';
          const colorHex =
            MUTED_COLORS.find((c) => c.id === (channel.color || 'default'))?.hex || '#ffffff';
          const isColored = channel.color && channel.color !== 'default';
          const hasChildren = (channels || []).some(
            (c) => c.parentId === channel.id && c.type !== 'dm'
          );
          const stateId = channel.id;
          const isExpanded = !!expandedIds[stateId];
          return (
            <SortableProjectItem
              key={channel.id}
              channel={channel}
              depth={depth}
              index={idx}
              isActive={isActive}
              isColored={isColored}
              channelColorHex={colorHex}
              displayName={channel.name}
              hasChildren={hasChildren}
              isExpanded={isExpanded}
              dispatch={dispatch}
              renderProjectTree={renderProjectTree}
              editingChannelId={editingChannelId}
              navigate={navigate}
              getProjectPath={getProjectPath}
              channels={channels}
              isTouch={isTouch}
              activeButtonsId={activeButtonsId}
              currentView={currentView}
            />
          );
        })}
      </SortableContext>
    );
  };

  const isWorkingChannel = window.location.hostname.includes('--working-');
  const envEmoji = useEmulator ? '🌵' : isWorkingChannel ? '🍁' : '🌴';

  return (
    <div className={`${isCompact ? 'w-0 border-r-0' : 'w-48'} transition-all duration-300 flex-shrink-0 bg-zinc-50 border-r border-zinc-200 flex flex-col h-full overflow-hidden select-none print:hidden relative`}>
      {/* Top Header */}
      <div className="h-14 px-4 flex items-center justify-between border-b border-zinc-200 bg-white">
        <div className="flex items-center gap-2 relative">
          <button
            data-testid="sidebar-home-btn"
            onClick={handleGoHome}
            className="hover:opacity-70 transition-opacity flex-shrink-0"
          >
            <h1 className="font-black text-blue-500 tracking-[0.5em] text-lg flex items-center gap-0">
              NYTM
            </h1>
          </button>
          
          <EnvDropdown
            envEmoji={envEmoji}
            user={user}
            profile={profile}
            saveUserProfile={saveUserProfile}
          />
        </div>
        <div className="flex items-center">
          <button
            data-testid="notifications-btn"
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            className={`p-1.5 rounded transition-colors flex-shrink-0 ${isNotificationsOpen ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-900/15 hover:text-zinc-900'}`}
          >
            <Bell size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Project List or Notifications */}
      {isNotificationsOpen ? (
        <div className="flex-1 overflow-y-auto bg-zinc-50 p-4 custom-scrollbar flex flex-col gap-4">
           <ListenBrainzFeed profile={profile} allUsers={allUsers} />
           <h2 className="text-[10px] font-black uppercase text-zinc-400 tracking-[0.2em] px-1">Notifications</h2>
           {(() => {
             const isModerator = profile?.email === 'brettgaddy@gmail.com' || user?.email === 'brettgaddy@gmail.com';
             const visibleNotifications = (notifications || []).filter(n => {
                if (n.channelId === 'moderators_only') return isModerator;
                return (channels || []).some(c => c.id === n.channelId);
             });
             if (visibleNotifications.length === 0) {
               return (
                 <div className="flex flex-col items-center justify-center py-20 text-center opacity-40">
                   <Bell size={32} strokeWidth={1.5} className="mb-4" />
                   <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">No new notifications</p>
                 </div>
               );
             }
             return (
               <div className="flex flex-col gap-2">
                 {visibleNotifications.map(n => {
                   let projectName = (channels || []).find(c => c.id === n.channelId)?.name || 'Project';
                   if (n.channelId === 'moderators_only') projectName = 'System Admin';
                   
                   let message = '';
                   if (n.type === 'task_added') {
                     message = `Added a task`;
                   } else if (n.type === 'task_edited') {
                     message = `Edited a task`;
                   } else if (n.type === 'task_priority') {
                     message = `Moved task to ${n.priority || 'Medium'} priority`;
                   } else if (n.type === 'event_change') {
                     message = `Database Update`;
                   }
                   
                   return (
                     <div key={n.id} className="bg-white border border-zinc-200 rounded-lg p-3 text-sm flex flex-col gap-1 shadow-sm">
                       <div className="flex items-center justify-between text-xs text-zinc-500 font-medium">
                         <span className="text-zinc-900 font-bold truncate pr-2">{projectName}</span>
                         <span className="flex-shrink-0 opacity-70">
                           {n.timestamp ? new Date(n.timestamp.seconds * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }) : ''}
                         </span>
                       </div>
                       <div className="text-zinc-700">
                         <span className="font-semibold text-zinc-900">{n.userName}</span> {message}
                       </div>
                       <div className="text-zinc-500 italic mt-1 line-clamp-2">
                         "{n.text}"
                       </div>
                     </div>
                   );
                 })}
               </div>
             );
           })()}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto py-4 space-y-4 px-3 custom-scrollbar">
          {/* All Projects Section */}
          <div data-testid="sidebar-projects-section">
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em]">
                Projects
              </div>
              <button
                data-testid="add-project-btn"
                onClick={() => dispatch({ type: 'OPEN_CREATE_CHANNEL', payload: null })}
                className="p-1 hover:bg-zinc-200 rounded text-zinc-400 hover:text-zinc-900 transition-colors flex-shrink-0"
              >
                <Plus size={12} strokeWidth={3} />
              </button>
            </div>
            {renderProjectTree(null)}
          </div>
        </div>
      )}

      <div className="px-2 py-4 border-t border-zinc-100 flex flex-col gap-1">
        {/* Calendar Section */}
        <div className="flex flex-col gap-1 px-1">
          <button
            onClick={() => {
              if (currentView === 'database') window.location.href = '/calendar' + window.location.search;
              else navigate('/calendar');
            }}
            className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${isCalendarActive ? 'bg-zinc-800 text-white shadow-md font-bold' : 'text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 font-medium'}`}
          >
            <CalendarIcon size={18} strokeWidth={2.5} />
            <span>Calendar</span>
          </button>
        </div>


        {/* More Links Section */}
        <div className="mt-2 mb-1">
          <button
            onClick={() => {
              const newVal = !isLinksExpanded;
              setIsLinksExpanded(newVal);
              localStorage.setItem('sidebarLinksExpanded', newVal);
            }}
            className="w-full flex items-center justify-between px-2 py-1 group"
          >
            <span className="text-[10px] font-black text-zinc-500 group-hover:text-zinc-700 uppercase tracking-widest flex items-center gap-1.5 transition-colors">
              <Plus size={12} className="opacity-50" /> Modules
            </span>
            <div className="flex items-center gap-2">
              {isLinksExpanded ? (
                <ChevronDown size={10} strokeWidth={3} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              ) : (
                <ChevronRight size={10} strokeWidth={3} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              )}
            </div>
          </button>
        </div>

        {isLinksExpanded && (
          <div className="flex flex-col gap-1 px-1 mb-2">
            {/* RSS Section */}
            <button
              onClick={() => {
                if (currentView === 'database') window.location.href = '/rss';
                else navigate('/rss');
              }}
              className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${isRSSActive ? 'bg-zinc-800 text-white shadow-md font-bold' : 'text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 font-medium'}`}
            >
              <Rss size={16} strokeWidth={2.5} />
              <span>RSS</span>
            </button>

            {/* Watch/Listen Section */}
            <button
              onClick={() => {
                if (currentView === 'database') window.location.href = '/watch';
                else navigate('/watch');
              }}
              className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${isWatchActive ? 'bg-zinc-800 text-white shadow-md font-bold' : 'text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 font-medium'}`}
            >
              <Youtube size={16} strokeWidth={2.5} />
              <span>Watch/Listen</span>
            </button>

            {/* Film/TV Section */}
            <button
              onClick={() => {
                if (currentView === 'database') window.location.href = '/film-tv';
                else navigate('/film-tv');
              }}
              className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${isFilmTVActive ? 'bg-zinc-800 text-white shadow-md font-bold' : 'text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 font-medium'}`}
            >
              <Film size={16} strokeWidth={2.5} />
              <span>Film/TV</span>
            </button>

            {/* Media Section */}
            <button
              onClick={() => {
                if (currentView === 'database') window.location.href = '/media';
                else navigate('/media');
              }}
              className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded transition-colors ${isMediaActive ? 'bg-zinc-800 text-white shadow-md font-bold' : 'text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 font-medium'}`}
            >
              <Camera size={16} strokeWidth={2.5} />
              <span>Media</span>
            </button>
          </div>
        )}

        {/* Friends Section */}
        <div data-testid="sidebar-people-section" className="mt-8 mb-4">
          <button
            onClick={() => {
              const newVal = !isFriendsListOpen;
              setIsFriendsListOpen(newVal);
              localStorage.setItem('sidebarFriendsOpen', newVal);
            }}
            className="w-full flex items-center justify-between px-4 mb-2 group"
          >
            <span className="text-[10px] font-black text-zinc-500 group-hover:text-zinc-700 uppercase tracking-widest flex items-center gap-1.5 transition-colors">
              <Users size={12} className="opacity-50" /> Friends
            </span>
            <div className="flex items-center gap-2">
              {isFriendsListOpen ? (
                <ChevronDown size={10} strokeWidth={3} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              ) : (
                <ChevronRight size={10} strokeWidth={3} className="text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              )}
            </div>
          </button>

          {isFriendsListOpen && (
            <div className="flex flex-col gap-0.5 max-h-48 overflow-y-auto">
              {combinedFriendsList.length > 0 ? combinedFriendsList.map((u) => (
                <DMUserItem
                  key={u.id}
                  targetUser={u}
                  user={user}
                  channels={channels}
                  activeChannelId={activeChannelId}
                  currentView={currentView}
                  dispatch={dispatch}
                  isChatOpen={isChatOpen}
                  toggleChat={toggleChat}
                  allUsers={allUsers}
                  handleShowProfile={handleShowProfile}
                  navigate={navigate}
                  isOnline={u.isOnline}
                />
              )) : (
                <div className="px-4 py-2 text-[10px] text-zinc-400 font-medium italic">
                  No friends added yet.
                </div>
              )}
            </div>
          )}      </div>
      </div>

      {/* User Section */}
      <div className="p-4 border-t border-zinc-200 bg-white">
        <button
          data-testid="settings-nav-btn"
          onClick={() => navigate('/settings')}
          className={`w-full flex items-center gap-3 p-2 rounded-xl transition-all hover:bg-zinc-100 group ${currentView === 'settings' ? 'bg-zinc-100' : ''}`}
        >
          <div className="flex flex-col items-start min-w-0 flex-1">
            <span className="text-[9px] font-bold text-zinc-700 truncate group-hover:text-zinc-900 transition-colors">
              {username}
            </span>
          </div>
          <Avatar src={userAvatar} size={28} className="shadow-sm transition-transform group-hover:scale-105" />
        </button>
      </div>
    </div>
  );
};

export default React.memo(Sidebar);
